import React from 'react';
import { Box, Button, ButtonBase } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { RollupStatus, computeRollup } from './useCountryGroupCollapse';

// specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-fe-country-group-folding.md. The shared, presentation-only
// pieces every foldable country group uses: the clickable header cell
// (chevron + name + click target, Goal 1), the collapsed roll-up badge
// (Goal 2: "never hides access state"), and the three bulk-action buttons
// (Goal 5).
//
// Deliberately i18n-agnostic: every visible/accessible string arrives
// already translated as a prop. Structure and the Users tables read theirs
// from the `superadmin` namespace (en/pl only -- de/cs/nl fall back to en,
// verified against i18n.ts); CompanyFilter reads its own from
// `superdashboardGhg` (same en/pl-only situation). Baking either namespace
// into this shared component would make it wrong for the other callers.

interface CountryGroupHeaderProps {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  // Fully-resolved aria-label/title, e.g. "Collapse Polska (3 branches)" --
  // see the namespace note above for why this is a resolved string, not a
  // translation key.
  actionLabel: string;
}

// Goal 1/BR-2. IMPORTANT: this header cell never carries a visible
// branch-count or roll-up badge of its own -- Structure's and Users' own
// pre-existing regression tests (structure.test.tsx, usersCountry.test.tsx)
// assert this cell's native `.textContent` equals exactly the country's
// plain label, a check that predates this feature and must keep passing
// open or collapsed. The chevron below is an SVG icon (contributes no text
// nodes), so it doesn't affect that; the branch count instead rides on
// `actionLabel` (aria-label/title, not text content), and the roll-up
// fraction only ever renders in the BODY cell a collapsed group replaces
// (CountryRollup below), never in the header row itself.
export default function CountryGroupHeader(props: CountryGroupHeaderProps) {
  const { label, isOpen, onToggle, actionLabel } = props;
  return (
    <ButtonBase
      onClick={onToggle}
      disableRipple
      aria-label={actionLabel}
      title={actionLabel}
      aria-expanded={isOpen}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        padding: '4px 6px',
        borderRadius: '4px',
        color: 'inherit',
        font: 'inherit',
        letterSpacing: 'inherit',
        textTransform: 'inherit',
      }}
    >
      <KeyboardArrowDownIcon
        fontSize="small"
        sx={{
          transition: 'transform 0.15s ease-in-out',
          transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
          flexShrink: 0,
        }}
      />
      <span>{label}</span>
    </ButtonBase>
  );
}

interface CountryRollupProps {
  on: number;
  total: number;
  // Fully-resolved accessible description, e.g. "2 of 3 branches enabled".
  ariaLabel: string;
}

const ROLLUP_COLOR: Record<RollupStatus, string> = {
  none: 'text.disabled',
  partial: 'warning.main',
  all: 'success.main',
};

// Goal 2/BR-2/BR-3/EC-2/EC-7: what a collapsed group's cell shows INSTEAD of
// its N branch switches -- never blank, never just a "collapsed" label.
// `total` must be the caller-scoped count the screen already computed
// (BR-3, group.branches.length), not refetched or looked up separately
// here.
export function CountryRollup(props: CountryRollupProps) {
  const { on, total, ariaLabel } = props;
  const { status } = computeRollup(on, total);
  return (
    <Box
      component="span"
      aria-label={ariaLabel}
      title={ariaLabel}
      sx={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontWeight: 700,
        fontSize: '0.85em',
        color: ROLLUP_COLOR[status],
      }}
    >
      {on}/{total}
    </Box>
  );
}

interface CountryGroupBulkControlsProps {
  collapseAllLabel: string;
  expandAllLabel: string;
  onCollapseAll: () => void;
  onExpandAll: () => void;
}

// Goal 5 (corrected): collapse all / expand all only. The original spec
// also had a "my countries" button; it was removed because it is inert for
// every account that can reach these screens -- scoping is enforced
// server-side, so a country admin never receives countries outside their
// own scope, and a MasterAdmin (the only account seeing all seventeen) has
// access to all of them, so "my countries" always resolves to "every
// country on screen" either way.
//
// Plain MUI Buttons -- the same component already used for e.g. Users'
// "New user" button (Users/index.tsx). Guardrail 1 (no redesign) reads as
// "reuse existing components, don't invent a new visual language", not "add
// nothing": bulk fold controls did not exist on any of these screens before
// this feature, so some new affordance is unavoidable.
export function CountryGroupBulkControls(props: CountryGroupBulkControlsProps) {
  const { collapseAllLabel, expandAllLabel, onCollapseAll, onExpandAll } =
    props;
  return (
    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      <Button size="small" variant="outlined" onClick={onCollapseAll}>
        {collapseAllLabel}
      </Button>
      <Button size="small" variant="outlined" onClick={onExpandAll}>
        {expandAllLabel}
      </Button>
    </Box>
  );
}
